<template>
  <el-card class="song-item" v-loading="loading">
    <!-- 替换音频 -->
    <verify-uploader
      v-if="!song.uploadPercent"
      class="replace"
      action="/action/upload?type=track"
      :accept="`.${trackType.join(',.')}`"
      :before-upload="handleMediaBefore"
      :on-progress="handleMediaProgress"
      :on-success="handleMediaSuccess"
      :on-error="handleMediaError"
      :show-file-list="false"
      :disabled="song.lockBasic"
    >
      <el-button type="primary" class="m-b-sm" :disabled="song.lockBasic">替换音频</el-button>
    </verify-uploader>
    <!-- 上传中 -->
    <div class="process" v-if="!song.mediaFinish || !song.media" @click.stop.prevent>
      <el-progress :percentage="song.uploadPercent" :show-text="false" class="progress"></el-progress>
      <span v-if="song.uploadPercent === 100">音频检测中..</span>
    </div>
    <player v-if="howlerData.src.length" :autoplay="false" :howler-data="howlerData"></player>

    <!-- 音频异常 -->
    <div v-if="!song.uploadPercent && song.hasError" @click.stop.prevent>
      <span class="error">音频存在严重音质问题, 请重新上传</span>
      <check-icon :track="{fileCheck: song.media.checkQuality}"></check-icon>
    </div>

    <el-button type="primary" class="m-b-sm" @click.stop="handleShowFlow">分发进度</el-button>
    <el-form
      ref="trackForms"
      :rules="rules"
      :model="song"
      :disabled="disabledEdit"
      :inline-message="true"
      :validate-on-rule-change="false"
      label-width="100px"
    >
      <el-tabs v-model="activeTab" type="card">
        <el-tab-pane label="基本信息" name="basic">
          <span slot="label">基本信息<i v-if="notFreeCompany && song.lockBasic" class="el-icon-lock" /><span class="danger-word">*</span></span>
          <el-form-item v-if="song.albumObj && song.albumObj.albumName" label="所属专辑" prop="albumId">
            {{ song.albumId }}
          </el-form-item>
          <warn-form-item label="歌曲名" prop="trackName">
            <el-input
              v-model.trim="song.trackName"
              placeholder="请输入音乐作品官方用名"
              class="form-field"
              v-trans-complex="'歌曲名'"
              :disabled="notFreeCompany && song.lockBasic"
            ></el-input>
          </warn-form-item>
          <el-form-item label="版本" prop="srcVersion">
            <el-select
              v-model="song.srcVersion"
              class="form-field"
              allow-create
              filterable
              :disabled="song.lockBasic"
            >
              <el-option v-for="(item, index) in srcVersionList" :key="index" :label="item.name" :value="item.name">
                {{ item.name }}
                <el-tooltip :content="item.desc" placement="top">
                  <i class="el-icon-info" />
                </el-tooltip>
              </el-option>
            </el-select>
            <span class="desc">请严格将版本信息写在版本字段</span>
          </el-form-item>
          <el-form-item label="副标题">
            <el-input
              v-model.trim="song.trackExtend.subTitle"
              class="form-field"
              placeholder="请输入影视类描述信息，如有多个用“|”分隔"
              :disabled="notFreeCompany && song.lockBasic"
            ></el-input>
          </el-form-item>
          <el-form-item label="翻译名" prop="transName">
            <el-input v-model.trim="song.transName" placeholder="请输入中文翻译名" class="form-field" :disabled="song.lockBasic"></el-input>
          </el-form-item>
          <el-form-item label="所属艺人" prop="trackSingerList">
            <search-singer
              :value.sync="song.trackSingerList"
              :default-options="song.trackSingerList"
              class="form-field"
              placeholder="输入艺人名搜索"
              :disabled="notFreeCompany && song.lockBasic"
            ></search-singer>
          </el-form-item>
          <el-form-item label="CD索引" prop="cdIndex">
            <el-select v-model="song.cdIndex" class="form-field">
              <el-option v-for="(name, id) in cdMap" :key="id" :label="name" :value="+id"></el-option>
            </el-select>
          </el-form-item>
          <el-form-item label="语言" prop="language">
            <el-select v-model="song.language" filterable class="form-field">
              <el-option
                v-for="(name, key) in languageMap"
                :key="key"
                :data-key="key"
                :value="+key"
                :label="name"
              ></el-option>
            </el-select>
          </el-form-item>

          <el-form-item label="一级流派" prop="genre">
            <el-select
              v-model="song.genre"
              class="form-field"
              @change="handleChangeGenre"
              placeholder="选择流派"
              multiple
            >
              <el-option
                v-for="(name, id) in genreMap"
                :key="id"
                :label="`${id}. ${name}`"
                :value="+id"
              ></el-option>
            </el-select>
          </el-form-item>

          <el-form-item
            v-if="checkShowSubGenre"
            label="二级流派"
            prop="trackExtend.subGenre"
          >
            <el-select
              v-model="song.trackExtend.subGenre"
              class="form-field"
              multiple
              filterable
              placeholder="选择流派"
            >
              <template
                v-for="(name, id) in subGenreMap"
              >
                <el-option
                  v-if="checkSubGenre(+id)"
                  :key="id"
                  :label="`${id}. ${name}`"
                  :value="+id"
                ></el-option>
              </template>
            </el-select>
          </el-form-item>

          <el-form-item label="ISRC编号">
            <el-input v-model.trim="song.isrc" class="form-field"></el-input>
          </el-form-item>
          <el-form-item label="ISWC编号">
            <el-input v-model.trim="song.iswc" class="form-field"></el-input>
          </el-form-item>
          <el-form-item label="歌曲编号">
            <el-input v-model.trim="song.trackNumber" class="form-field"></el-input>
          </el-form-item>
          <el-form-item label="发行时间" prop="publicTime">
            <el-date-picker
              v-model="song.publicTime"
              type="datetime"
              placeholder="实际发行时间"
              class="form-field"
              value-format="yyyy-MM-dd HH:mm:ss"
              :disabled="notFreeCompany && song.lockBasic"
              @change="handleDateChange($event, '发行时间')"
            ></el-date-picker>
          </el-form-item>
          <el-form-item label="上线时间" prop="saleTime">
            <el-date-picker
              v-model="song.saleTime"
              type="datetime"
              placeholder="上线时间"
              class="form-field"
              value-format="yyyy-MM-dd HH:mm:ss"
              @change="handleDateChange($event, '上线时间')"
            ></el-date-picker>
          </el-form-item>
          <el-form-item prop="trackPayment.paymentMode">
            <template v-slot:label>
              付费模式<i v-if="notFreeCompany && song.lockBasic" class="el-icon-lock" />
            </template>
            <el-select
              v-model="song.trackPayment.paymentMode"
              clearable
              filterable
              :disabled="(notFreeCompany && song.lockBasic) || (song.trackPayment.paymentMode === DIGITAL_PAYMENT_MODE)"
              class="form-field"
              @change="handlePaymentChange(song)"
            >
              <el-option
                v-for="(name, key) in paymentMap"
                :key="key"
                :data-key="key"
                :label="name"
                :value="+key"
              ></el-option>
            </el-select>
          </el-form-item>
          <el-form-item v-if="song.trackPayment.paymentMode === TWO_TO_THREE" prop="trackPayment.freeDuration">
            <template #label>
              限免期
              <el-tooltip placement="top" content="限免天数即代表歌曲上架后，在规定天数内限时2.0，后转入3.0">
                <i class="el-icon-info" />
              </el-tooltip>
            </template>
            <el-input-number
              v-model="song.trackPayment.freeDuration"
              class="form-field"
              :min="0"
              :max="1000"
              :controls="false"
              :step="1"
            ></el-input-number>
          </el-form-item>
          <el-form-item prop="payFileList" label="付费模式文件" v-if="!song.lockBasic && song.trackId">
            <file-upload text="付费模式文件" :disabled="notFreeCompany && song.lockBasic" :file-list.sync="song.payFileList"></file-upload>
          </el-form-item>
          <el-form-item label="版权证明" v-if="!song.lockBasic && song.trackId">
            <el-form-item prop="audioFileList">
              <file-upload text="录音文件" :disabled="notFreeCompany && song.lockBasic" :file-list.sync="song.audioFileList"></file-upload>
            </el-form-item>
            <el-form-item prop="actorFileList">
              <file-upload text="表演者文件" :disabled="notFreeCompany && song.lockBasic" :file-list.sync="song.actorFileList"></file-upload>
            </el-form-item>
          </el-form-item>
          <lyric
            ref="lyric"
            :lyric="song.lyricData"
            :media="song.media"
            :track="song"
            :disabled="song.lockBasic"
          ></lyric>
        </el-tab-pane>

        <el-tab-pane label="扩展信息" name="extend">
          <span slot="label">扩展信息<i v-if="song.lockBasic" class="el-icon-lock" /></span>
          <el-form-item label="作词文本" prop="trackExtend.wordAuthor">
            <el-input
              v-model.trim="song.trackExtend.wordAuthor"
              v-trans-complex="'作词'"
              class="form-field"
              placeholder="多位作者请用“|”分隔"
              :disabled="notFreeCompany && song.lockBasic"
            ></el-input>
          </el-form-item>
          <el-form-item label="作词ID" prop="trackLyricistList">
            <search-singer
              :value.sync="song.trackLyricistList"
              :default-options="song.trackLyricistList"
              class="form-field"
              placeholder="输入艺人名搜索"
              :disabled="notFreeCompany && song.lockBasic"
            >
              <template v-slot:dropdown>
                <div style="padding: 8px;">
                  找不到艺人？可以选择
                  <router-link :to="{name: 'singerList'}" target="_blank">新建艺人</router-link>
                </div>
              </template>
            </search-singer>
          </el-form-item>
          <el-form-item label="作曲文本">
            <el-input
              v-model.trim="song.trackExtend.musicAuthor"
              v-trans-complex="'作曲'"
              class="form-field"
              placeholder="多位作者请用“|”分隔"
              :disabled="notFreeCompany && song.lockBasic"
            ></el-input>
          </el-form-item>
          <el-form-item label="作曲ID" prop="trackComposerList">
            <search-singer
              :value.sync="song.trackComposerList"
              :default-options="song.trackComposerList"
              class="form-field"
              placeholder="输入艺人名搜索"
              :disabled="notFreeCompany && song.lockBasic"
            >
              <template v-slot:dropdown>
                <div style="padding: 8px;">
                  找不到艺人？可以选择
                  <router-link :to="{name: 'singerList'}" target="_blank">新建艺人</router-link>
                </div>
              </template>
            </search-singer>
          </el-form-item>
          <el-form-item label="编曲文本">
            <el-input
              v-model.trim="song.trackExtend.arrangeAuthor"
              v-trans-complex="'编曲'"
              class="form-field"
              placeholder="多位作者请用“|”分隔"
            ></el-input>
          </el-form-item>
          <el-form-item label="编曲ID" prop="trackArrangerList">
            <search-singer
              :value.sync="song.trackArrangerList"
              :default-options="song.trackArrangerList"
              class="form-field"
              placeholder="输入艺人名搜索"
            >
              <template v-slot:dropdown>
                <div style="padding: 8px;">
                  找不到艺人？可以选择
                  <router-link :to="{name: 'singerList'}" target="_blank">新建艺人</router-link>
                </div>
              </template>
            </search-singer>
          </el-form-item>

          <el-form-item label="制作人文本">
            <el-input
              v-model.trim="song.trackExtend.producer"
              v-trans-complex="'制作人'"
              class="form-field"
              placeholder="多位作者请用“|”分隔"
            ></el-input>
          </el-form-item>
          <el-form-item label="制作人ID" prop="trackProducerList">
            <search-singer
              :value.sync="song.trackProducerList"
              :default-options="song.trackProducerList"
              class="form-field"
              placeholder="输入艺人名搜索"
            >
              <template v-slot:dropdown>
                <div style="padding: 8px;">
                  找不到艺人？可以选择
                  <router-link :to="{name: 'singerList'}" target="_blank">新建艺人</router-link>
                </div>
              </template>
            </search-singer>
          </el-form-item>

          <!-- 版权公司是 userCompany 的 cpCompanyId 字段 -->
          <el-form-item v-if="userCompany.cpCompanyId === 17548 || song.trackCopyright.originCpCompany.companyId === 17548" label="制作公司">
            <el-input v-model.trim="song.trackExtend.productionCompanyName" class="form-field" placeholder="填写制作公司"></el-input>
          </el-form-item>
        </el-tab-pane>

        <el-tab-pane label="歌曲乐器" name="instrument">
          <instrument ref="inst" :ins="song.trackInstrument"></instrument>
        </el-tab-pane>

        <el-tab-pane label="录音版权" name="copyright">
          <span slot="label">录音版权<i v-if="notFreeCompany && song.lockCopyright" class="el-icon-lock" /><span class="danger-word">*</span></span>
          <el-form-item label="原始版权公司" prop="trackCopyright.originCpCompany.companyName" label-width="110px">
            <el-autocomplete
              v-model="song.trackCopyright.originCpCompany.companyName"
              :fetch-suggestions="originCompanySearch('trackCopyright')"
              :disabled="notFreeCompany && song.lockCopyright"
              class="form-field"
              value-key="companyName"
              placeholder="请输入内容"
              @select="handleOriginCompanySelect('trackCopyright', $event)"
            ></el-autocomplete>
            <el-tooltip placement="top">
              <template v-slot:content>
                原始版权公司即领接权公司，当上传内容为代理内容时需填写
              </template>
              <i class="el-icon-question area-info" />
            </el-tooltip>
          </el-form-item>
          <el-form-item label="授权起始时间" prop="trackCopyright.date" label-width="110px">
            <el-date-picker
              v-model="song.trackCopyright.date"
              :disabled="notFreeCompany && song.lockCopyright"
              type="daterange"
              class="form-field"
              range-separator="至"
              value-format="yyyy-MM-dd"
              start-placeholder="开始日期"
              end-placeholder="结束日期"
            >
            </el-date-picker>
          </el-form-item>

          <!-- 公司类型 2-免费授权 不显示,默认 0 -->
          <el-form-item v-show="userCompany.type !== 2 || isInnerUser" label="版权来源" prop="trackCopyright.cpSource" label-width="110px">
            <el-select
              v-model="song.trackCopyright.cpSource"
              :disabled="notFreeCompany && song.lockCopyright"
              class="form-field"
              filterable
            >
              <el-option
                v-for="(name, id) in cpSourceMap"
                :key="id"
                :data-key="id"
                :label="name"
                :value="+id"
              ></el-option>
            </el-select>
          </el-form-item>

          <el-form-item label="海外总代理" prop="trackCopyright.isOverseaOnly" label-width="110px">
            <el-radio-group v-model="song.trackCopyright.isOverseaOnly" :disabled="notFreeCompany && song.lockCopyright">
              <el-radio v-for="(name, id) in isOverseaMap" :key="id" :label="+id">{{ name }}</el-radio>
            </el-radio-group>
          </el-form-item>
          <el-form-item label="授权区域" prop="trackCopyright.copyrightArea" label-width="110px">
            <el-select
              v-model="song.trackCopyright.copyrightArea"
              :disabled="notFreeCompany && song.lockCopyright"
              style="width: 400px"
              multiple
              filterable
            >
              <el-option
                v-for="(name, id) in copyrightAreaMap"
                :key="id"
                :data-key="id"
                :label="name"
                :value="+id"
              ></el-option>
            </el-select>
            <el-tooltip placement="top">
              <template v-slot:content>
                <ul>
                  <li>选择任意地区：表示授权区域范围是【选中的地区】</li>
                  <li>单选“全世界“时授权范围即为全世界；选“全世界”+其他某地区时，授权范围为全世界除其他某地区</li>
                </ul>
              </template>
              <i class="el-icon-question area-info" />
            </el-tooltip>
          </el-form-item>

          <!-- 公司类型 2-免费授权 不显示,默认 0 -->
          <el-form-item v-show="userCompany.type !== 2 || isInnerUser" label="授权形式" prop="trackCopyright.isOnly" label-width="110px">
            <el-radio-group v-model="song.trackCopyright.isOnly" :disabled="notFreeCompany && song.lockCopyright">
              <el-radio v-for="(name, id) in isOnlyMap" :key="id" :label="+id">{{ name }}</el-radio>
            </el-radio-group>
          </el-form-item>

          <el-form-item
            v-if="song.trackCopyright.isOnly === 0"
            label="网易授权到期日期"
            label-width="110px"
            prop="trackCopyrightCompetitor.copyrightTo"
          >
            <el-date-picker
              v-model="song.trackCopyrightCompetitor.copyrightTo"
              type="datetime"
              style="width: 380px"
              placeholder="指作品在网易平台授权截止时间"
              value-format="yyyy-MM-dd HH:mm:ss"
            ></el-date-picker>
          </el-form-item>

          <el-form-item v-show="song.trackCopyright.isOnly === 0" label="首发期限" label-width="110px">
            <el-date-picker
              v-model="song.firstPublicTime"
              type="datetime"
              placeholder="首发期限"
              class="form-field"
              value-format="yyyy-MM-dd HH:mm:ss"
              :disabled="notFreeCompany && song.lockCopyright"
              :picker-options="firstPublicTimeOptions"
              @change="handleDateChange($event, '首发期限')"
            ></el-date-picker>
            <el-tooltip content="非必选，指从内容上线时间到首发期限时间段内为独家首发" placement="top">
              <i class="el-icon-question area-info" />
            </el-tooltip>
          </el-form-item>

          <!-- 公司类型不是 2-免费授权 and  -->
          <el-form-item
            v-show="(userCompany.type !== 2 || isInnerUser) && song.trackCopyright.isOnly === 1"
            label="可否转授权"
            prop="trackCopyright.canAuth"
            label-width="110px"
          >
            <el-radio-group v-model="song.trackCopyright.canAuth" :disabled="notFreeCompany && song.lockCopyright">
              <el-radio v-for="(name, id) in canAuthMap" :key="id" :label="+id">{{ name }}</el-radio>
            </el-radio-group>
          </el-form-item>
          <el-form-item label="授权关系" prop="trackCopyright.isAgent" label-width="110px">
            <el-radio-group v-model="song.trackCopyright.isAgent" :disabled="notFreeCompany && song.lockCopyright">
              <el-radio v-for="(name, id) in isAgentMap" :key="id" :label="+id">{{ name }}</el-radio>
            </el-radio-group>
          </el-form-item>

          <el-form-item label="版权证明" v-if="!song.lockCopyright && song.trackId">
            <el-form-item prop="audioFileList">
              <file-upload text="录音文件" :disabled="notFreeCompany && song.lockCopyright" :file-list.sync="song.audioFileList"></file-upload>
            </el-form-item>
            <el-form-item prop="actorFileList">
              <file-upload text="表演者文件" :disabled="notFreeCompany && song.lockCopyright" :file-list.sync="song.actorFileList"></file-upload>
            </el-form-item>
          </el-form-item>
        </el-tab-pane>

        <el-tab-pane label="词版权" name="word">
          <span slot="label">词版权<i v-if="notFreeCompany && song.lockWord" class="el-icon-lock" /></span>
          <div><span style="color: red;">*</span>请按照实际版权情况填写，无词曲版权则无需填写</div>
          <el-button type="primary" :disabled="notFreeCompany && song.lockWord" @click="handleAddWM('word')">创建词版权</el-button>
          <el-table v-if="song.trackWordCopyrightList.length" :data="song.trackWordCopyrightList">
            <!-- <el-table-column label="id" prop="id"></el-table-column> -->
            <el-table-column label="原始版权公司" prop="originCpCompany.companyName"></el-table-column>
            <el-table-column label="授权开始时间" prop="date[0]"></el-table-column>
            <el-table-column label="授权结束时间" prop="date[1]"></el-table-column>
            <el-table-column label="授权形式" prop="isOnly">
              <template v-slot="{ row }">
                {{ isOnlyMap[row.isOnly] }}
              </template>
            </el-table-column>
            <el-table-column label="操作">
              <template v-slot="{ row, $index }">
                <el-button type="text" :disabled="notFreeCompany && song.lockWord" @click="handleEdit('word', row)">编辑</el-button>
                <el-button type="text" :disabled="notFreeCompany && song.lockWord" @click="handleRemove('word', $index)">删除</el-button>
              </template>
            </el-table-column>
          </el-table>
        </el-tab-pane>

        <el-tab-pane label="曲版权" name="music">
          <span slot="label">曲版权<i v-if="notFreeCompany && song.lockMusic" class="el-icon-lock" /></span>
          <div><span style="color: red;">*</span>请按照实际版权情况填写，无词曲版权则无需填写</div>
          <el-button type="primary" :disabled="notFreeCompany && song.lockMusic" @click="handleAddWM('music')">创建曲版权</el-button>
          <el-table v-if="song.trackMusicCopyrightList.length" :data="song.trackMusicCopyrightList">
            <el-table-column label="原始版权公司" prop="originCpCompany.companyName"></el-table-column>
            <el-table-column label="授权开始时间" prop="date[0]"></el-table-column>
            <el-table-column label="授权结束时间" prop="date[1]"></el-table-column>
            <el-table-column label="授权形式" prop="isOnly">
              <template v-slot="{ row }">
                {{ isOnlyMap[row.isOnly] }}
              </template>
            </el-table-column>
            <el-table-column label="操作">
              <template v-slot="{ row, $index }">
                <el-button type="text" :disabled="notFreeCompany && song.lockMusic" @click="handleEdit('music', row)">编辑</el-button>
                <el-button type="text" :disabled="notFreeCompany && song.lockMusic" @click="handleRemove('music', $index)">删除</el-button>
              </template>
            </el-table-column>
          </el-table>
        </el-tab-pane>
      </el-tabs>
      <span
        v-if="['basic', 'word', 'music', 'copyright'].includes(activeTab)"
        class="apply-icon"
        @click.stop="handleApply"
      >
        <i class="el-icon-key" />申请修改
      </span>
    </el-form>

    <div class="center m-t-lg">
      <el-button @click="handleReset">重 置</el-button>
      <el-button type="primary" @click="handleSave">保 存</el-button>
    </div>

    <!-- 编辑版权 -->
    <el-dialog
      v-if="dialogShow"
      v-draggable
      @close="handleEditClose"
      :title="dialogTitle"
      :append-to-body="true"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      :visible.sync="dialogShow"
    >
      <el-form
        ref="editCopyright"
        :model="editCopyright"
        :rules="wmRules"
        :inline-message="true"
        label-position="right"
        label-width="110px"
      >
        <el-form-item label="原始版权公司" prop="originCpCompany.companyName">
          <el-autocomplete
            class="form-field"
            v-model="editCopyright.originCpCompany.companyName"
            :fetch-suggestions="originCompanySearch('editCopyright')"
            value-key="companyName"
            placeholder="请输入内容"
            @select="handleOriginCompanySelect('editCopyright', $event)"
          ></el-autocomplete>
          <el-tooltip placement="top">
            <template v-slot:content>
              原始版权公司即领接权公司，当上传内容为代理内容时需填写
            </template>
            <i class="el-icon-question area-info" />
          </el-tooltip>
        </el-form-item>
        <el-form-item label="授权起始时间" prop="date">
          <el-date-picker
            v-model="editCopyright.date"
            type="daterange"
            class="form-field"
            range-separator="至"
            value-format="yyyy-MM-dd"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
          >
          </el-date-picker>
        </el-form-item>
        <el-form-item label="份额" prop="cpPercent">
          <el-input-number v-model="editCopyright.cpPercent" :controls="false" :min="0"></el-input-number>
          <el-tooltip placement="top">
            <template v-slot:content>
              <div>份额区间：0&lt;词/曲份额≤100，</div>
              <div>请用100表示完整权利，如填写词份额50，则代表仅有50%的词权利。</div>
            </template>
            <i class="el-icon-question area-info" />
          </el-tooltip>
        </el-form-item>
        <el-form-item v-show="userCompany.type !== 2 || isInnerUser" label="版权来源" prop="cpSource">
          <el-select
            v-model="editCopyright.cpSource"
            class="form-field"
            filterable
          >
            <el-option
              v-for="(name, id) in cpSourceMap"
              :key="id"
              :data-key="id"
              :label="name"
              :value="+id"
            ></el-option>
          </el-select>
        </el-form-item>

        <el-form-item label="签约类型" prop="contractType">
          <el-select
            v-model="editCopyright.contractType"
            style="width: 400px"
            filterable
          >
            <el-option
              v-for="(name, id) in contractTypeMap"
              :key="id"
              :label="name"
              :value="+id"
            ></el-option>
          </el-select>
          <el-tooltip placement="top">
            <template v-slot:content>
              <ul>
                <li>录音附带：拥有歌曲词/曲版权前提下，选择相应授权形式。如仅拥有该专辑下歌曲的词/曲版权，选择录音附带；</li>
                <li>词曲授权：若可覆盖其他版本录音，则选择词曲授权</li>
              </ul>
            </template>
            <i class="el-icon-question area-info" />
          </el-tooltip>
        </el-form-item>

        <el-form-item label="海外总代理" prop="isOverseaOnly">
          <el-radio-group v-model="editCopyright.isOverseaOnly">
            <el-radio v-for="(name, id) in isOverseaMap" :key="id" :label="+id">{{ name }}</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="授权区域" prop="copyrightArea">
          <el-select
            v-model="editCopyright.copyrightArea"
            style="width: 400px"
            multiple
            filterable
          >
            <el-option
              v-for="(name, id) in copyrightAreaMap"
              :key="id"
              :label="name"
              :value="+id"
            ></el-option>
          </el-select>
          <el-tooltip placement="top">
            <template v-slot:content>
              <ul>
                <li>选择任意地区：表示授权区域范围是【选中的地区】</li>
                <li>单选“全世界“时授权范围即为全世界；选“全世界”+其他某地区时，授权范围为全世界除其他某地区</li>
              </ul>
            </template>
            <i class="el-icon-question area-info" />
          </el-tooltip>
        </el-form-item>

        <el-form-item
          v-show="userCompany.type !== 2 || isInnerUser"
          label="授权形式"
          prop="isOnly"
        >
          <el-radio-group v-model="editCopyright.isOnly">
            <el-radio v-for="(name, id) in isOnlyMap" :key="id" :label="+id">{{ name }}</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="可维权" prop="licenseValid">
          <el-checkbox v-model="editCopyright.licenseValid" :true-label="1" :false-label="2"></el-checkbox>
        </el-form-item>
        <el-form-item
          v-show="(userCompany.type !== 2 || isInnerUser) && song.trackCopyright.isOnly === 1"
          label="可否转授权"
          prop="canAuth"
        >
          <el-radio-group v-model="editCopyright.canAuth">
            <el-radio v-for="(name, id) in canAuthMap" :key="id" :label="+id">{{ name }}</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="授权关系" prop="isAgent">
          <el-radio-group v-model="editCopyright.isAgent">
            <el-radio v-for="(name, id) in isAgentMap" :key="id" :label="+id">{{ name }}</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-form-item
          label="可翻唱"
          prop="canCover"
          v-if="ixYxCompanyUser"
        >
          <el-checkbox v-model="editCopyright.canCover" :true-label="1" :false-label="2"></el-checkbox>
        </el-form-item>
        <el-form-item label="版权证明" required>
          <el-form-item v-if="editType === 'word'" prop="wordFileList">
            <file-upload text="词版权证明文件" :file-list.sync="editCopyright.wordFileList"></file-upload>
          </el-form-item>
          <el-form-item v-else-if="editType === 'music'" prop="musicFileList">
            <file-upload text="曲版权证明文件" :file-list.sync="editCopyright.musicFileList"></file-upload>
          </el-form-item>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button type="ghost" @click="handleEditClose">取 消</el-button>
        <el-button type="primary" @click="handleSaveEdit">保 存</el-button>
      </div>
    </el-dialog>

    <!-- 分发进度 -->
    <el-dialog
      v-if="showFlow"
      title="分发进度"
      append-to-body
      width="400px"
      :close-on-click-modal="false"
      :visible.sync="showFlow"
      @close="handleCloseFlow"
    >
      <flow
        :status="trackFlowStatus"
        :bu-trace="flowTrack.buTrace"
        @cancel="handleCloseFlow"
      ></flow>
    </el-dialog>
  </el-card>
</template>

<script>
import { mapGetters } from 'vuex'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import cloneDeep from 'lodash-es/cloneDeep'
import SearchSinger from '@/components/SearchSinger'
import VerifyUploader from '@/components/VerifyUploader'
import Player from '@/components/Player'
import trackService from '@/api/track'
import { WarnError, SBCcaseList, trackType } from '@/utils/constants'
import { validateFileExt } from '@/utils/validate'
import { isAudioCheckError } from '@/utils/fileUtil'
import Song from '@/models/Song'
import { ROLE_TYPE_ENUM } from '@/views/person/const'
import Lyric from '@/views/album/components/Lyric'
import Instrument from '@/views/album/components/Instrument'
import FileUpload from '@/views/album/components/FileUpload'
import Flow from './components/Flow'
import {
  languageMap, cdMap, isOnlyMap, canAuthMap, paymentMap, licenseFileType,
  isAgentMap, cpSourceMap, copyrightAreaMap, defaultWMCopyright, lyricList,
  srcGenreList, isOverseaMap, contractTypeMap, subGenreMap, genreMap, hasSubGenreSet,
  trackStatusToAlbumStatus,
  srcVersionList
} from '@/views/album/mixin.js'
import {
  validateError,
  fileFieldConfigList,
  copyrightToFE,
  cpToBackend,
  checkAndApplyModify,
  diffArray,
  getDiffObj,
} from './const'

dayjs.extend(customParseFormat)

export default {
  name: 'SongItem',
  components: {
    SearchSinger,
    Lyric,
    Instrument,
    FileUpload,
    VerifyUploader,
    Player,
    Flow
  },
  data() {
    // http://tapd.oa.com/tme_music_lib/prong/stories/view/1020369272858599991
    const nameValidator = (rule, value, callback) => {
      const { trackSingerList } = this.song
      const ruleList = [
        {
          errorMsg: '歌曲名不得包含发行信息',
          test() {
            return /\d{4,}/g.test(value)
          }
        },
        {
          errorMsg: '歌曲名不得包含曲序/编号信息',
          test() {
            return /^\d.*/g.test(value)
          }
        },
        {
          errorMsg: '歌曲名不得包含艺人信息',
          test() {
            return trackSingerList.map(s => s.singerName).some(name => value.includes(name))
          }
        },
        {
          errorMsg: '影视信息请填写在描述字段',
          test() {
            return validateError.moiveErrorList.some(movie => value.includes(`(${movie})`) || value.includes(`（${movie}）`))
          }
        },
        {
          errorMsg: '串烧歌曲请使用加号“+”或斜杆“/”分隔',
          test() {
            return validateError.seriesErrorList.some(series => value.includes(series))
          }
        },
        {
          errorMsg: '歌曲名请勿使用广义名称',
          test() {
            return ['曲目', '器乐'].some(universal => value.includes(universal))
          }
        },
        {
          errorMsg: '非官方要求请使用英文半角符号',
          test() {
            return SBCcaseList.some(sbc => value.includes(sbc))
          }
        },

      ]

      for (const rule of ruleList) {
        if (rule.test()) {
          return callback(new WarnError(rule.errorMsg))
        }
      }

      callback()
    }

    const nameValidator2 = (rule, value, callback) => {
      const ruleList = [
        {
          errorMsg: '歌曲名不得包含版本信息',
          test() {
            console.log('validate')
            // 歌曲名命中指定版本文本
            if (validateError.trackErrorVersionList.some(version => value.toLowerCase().includes(version))) {
              return true
            }

            // 带括号的命中指定版本文本
            return validateError.bracketRegs.some(reg => {
              const match = reg.exec(value)
              console.log(match, reg)
              return match && match.length && validateError.versionErrorList.some(text => match[0].toLowerCase().includes(text))
            })
          }
        }
      ]

      for (const rule of ruleList) {
        if (rule.test()) {
          return callback(new Error(rule.errorMsg))
        }
      }

      callback()
    }

    const validActorFileList = (rule, value, callback) => {
      // 核心信息修改
      const isSingerChanged = !!diffArray(this.song.oldLockObj.trackSingerList, this.song.trackSingerList)
      if (!this.song.lockBasic && value.length === 0 && isSingerChanged) {
        return callback(new Error('必须上传表演者文件'))
      } else if (!this.song.lockCopyright && value.length === 0 && this.song.audioFileList.length === 0) { // 录音版权修改
        return callback(new Error('必须上传录音文件/表演者文件之一'))
      }
      callback()
    }

    const validAudioFileList = (rule, value, callback) => {
      // 核心信息修改
      const isChanged = !!getDiffObj(this.song.oldLockObj, this.song, ['trackName', 'srcVersion'])
      if (!this.song.lockBasic && value.length === 0 && isChanged) {
        return callback(new Error('必须上传录音文件'))
      }

      callback()
    }

    return {
      languageMap,
      cdMap,
      paymentMap,
      isOnlyMap,
      canAuthMap,
      isAgentMap,
      cpSourceMap,
      copyrightAreaMap,
      srcGenreList,
      isOverseaMap,
      licenseFileType,
      contractTypeMap,
      subGenreMap,
      genreMap,
      trackType,
      trackStatusToAlbumStatus,
      srcVersionList,

      activeTab: 'basic',
      editType: 'word',
      trackId: 0,
      dialogShow: false,
      editCopyright: null,
      loading: false,
      rules: {
        trackName: [
          { required: true, message: '请输入歌曲名', trigger: 'blur' },
          { validator: nameValidator, warnType: 'notify', trigger: 'blur' },
          { validator: nameValidator2, warnType: 'notify', trigger: 'blur' },
        ],
        genre: [
          { required: true, message: '请填写一级流派', trigger: 'change' }
        ],
        'trackExtend.subGenre': [
          { required: true, message: '请填写二级流派', trigger: 'change' }
        ],
        trackSingerList: [
          { required: true, message: '至少含有一个艺人', trigger: 'blur' }
        ],
        language: [
          { type: 'number', required: true, message: '请选择语言', trigger: 'change' }
        ],
        cdIndex: [
          { type: 'number', required: true, message: '请选择CD', trigger: 'change' }
        ],
        saleTime: [
          { required: true, message: '请选择上线时间', trigger: 'blur' }
        ],
        publicTime: [
          { required: true, message: '请选择发行时间', trigger: 'blur' }
        ],
        'trackCopyright.date': [
          { type: 'array', required: true, message: '请选择授权时间', trigger: 'change' }
        ],
        'trackCopyright.cpSource': [
          { type: 'number', required: true, message: '请选择授权来源', trigger: 'change' }
        ],
        'trackCopyright.copyrightArea': [
          { type: 'array', required: true, message: '请选择授权区域', trigger: 'change' }
        ],
        'trackCopyright.isOnly': [
          { type: 'number', required: true, message: '请选择授权形式', trigger: 'change' }
        ],
        'trackCopyright.isOverseaOnly': [
          { type: 'number', required: true, message: '请选择是否海外总代理', trigger: 'change' }
        ],
        'trackCopyright.canAuth': [
          { type: 'number', required: true, message: '请选择是否可转授', trigger: 'change' }
        ],
        'trackCopyright.isAgent': [
          { type: 'number', required: true, message: '请选择授权关系', trigger: 'change' }
        ],
        'trackPayment.paymentMode': [
          { type: 'number', required: true, message: '请选择付费类型', trigger: 'change' }
        ],
        audioFileList: [
          { validator: validAudioFileList, trigger: 'change' }
        ],
        actorFileList: [
          { validator: validActorFileList, trigger: 'change' }
        ]
      },
      wmRules: {
        date: [
          { type: 'array', required: true, message: '请选择授权时间', trigger: 'change' }
        ],
        cpPercent: [
          { type: 'number', required: true, message: '请填写份额', trigger: 'blur' },
          { type: 'number', max: 100, message: '份额不能大于100', trigger: 'blur' },
          { type: 'number', min: 1, message: '份额不能小于等于0', trigger: 'blur' }
        ],
        cpSource: [
          { type: 'number', required: true, message: '请选择授权来源', trigger: 'change' }
        ],
        copyrightArea: [
          { type: 'array', required: true, message: '请选择授权区域', trigger: 'change' }
        ],
        isOnly: [
          { type: 'number', required: true, message: '请选择授权形式', trigger: 'change' }
        ],
        canAuth: [
          { type: 'number', required: true, message: '请选择是否可转授', trigger: 'change' }
        ],
        isAgent: [
          { type: 'number', required: true, message: '请选择授权关系', trigger: 'change' }
        ],
        isOverseaOnly: [
          { type: 'number', required: true, message: '请选择是否海外总代理', trigger: 'change' }
        ],
        contractType: [
          { type: 'number', required: true, message: '请选择签约类型', trigger: 'change' }
        ],
        musicFileList: [
          { type: 'array', required: true, message: '请上传文件', trigger: 'change' }
        ],
        wordFileList: [
          { type: 'array', required: true, message: '请上传文件', trigger: 'change' }
        ]
      },
      firstPublicTimeOptions: {
        disabledDate: (time) => {
          const timeObj = dayjs(new Date(time))
          const saleTime = dayjs(this.song.saleTime, 'YYYY-MM-DD HH:mm:ss')

          return timeObj.isBefore(saleTime)
        }
      },
      flowTrack: null,
      showFlow: false,
      song: new Song(),
      howlerData: { src: [] }
    }
  },
  computed: {
    ...mapGetters([
      'userInfo',
      'originCompanyList',
      'copyrightCompanyList',
      'ixYxCompanyUser'
    ]),
    /**
     * @returns {String}
     */
    dialogTitle() {
      return (!this.editCopyright.id ? '创建' : '修改') + (this.editType === 'word' ? '词版权' : '曲版权')
    },
    /**
     * @returns {Boolean}
     */
    isInnerUser() {
      return ![ROLE_TYPE_ENUM.COMPANY.code, ROLE_TYPE_ENUM.OTHER.code].includes(this.userInfo.roleType)
    },
    /**
     * @returns {Object}
     */
    userCompany() {
      return this.copyrightCompanyList[0] || {}
    },
    /**
     * @returns {Boolean}
     */
    notFreeCompany() {
      return this.userCompany.type !== 2
    },
    /**
     * @returns {Boolean}
     */
    hasBasicAuth() {
      return this.$store.getters.isCompany || this.$store.getters.isEditor
    },
    /**
     * @returns {Boolean}
     */
    disabledEdit() {
      const { copyrightCompanyList, song: track } = this
      return !this.hasBasicAuth || track.trackCopyright.cpStatus !== 1 ||
        !track.trackCopyright.cpCompany ||
        !copyrightCompanyList.find(item => item.cpCompanyId === track.trackCopyright.cpCompany.companyId)
    },
    /**
     * @returns {Boolean}
     */
    checkShowSubGenre() {
      return this.song.genre.some(value => hasSubGenreSet.has(value))
    },
    /**
     * @returns {Number}
     */
    DIGITAL_PAYMENT_MODE() {
      return 14
    },
    /**
     * @returns {Number}
     */
    TWO_TO_THREE() {
      return 15
    },
    /**
     * @returns {Boolean}
    */
    trackFlowStatus() {
      const { flowTrack } = this
      return flowTrack.album.status === 3 || flowTrack.album.status === 4 ? flowTrack.album.status : trackStatusToAlbumStatus[flowTrack.status]
    }
  },
  watch: {
    'song.trackCopyright.isOnly'(newVal) {
      if (newVal === 0) {
        this.song.trackCopyright.canAuth = 0
      }
    },
    'editCopyright.isOnly'(newVal) {
      if (newVal === 0) {
        this.editCopyright.canAuth = 0
      } else if (newVal === 1 && this.editCopyright.isAgent === 0) {
        this.editCopyright.licenseValid = 1
      }
    },
    'editCopyright.isAgent'(newVal) {
      if (newVal === 0 && this.editCopyright.isOnly === 1) {
        this.editCopyright.licenseValid = 1
      }
    }
  },
  created() {
    let trackId
    if ((trackId = this.$route.query.trackId)) {
      this.trackId = trackId
      this.fetchData(trackId)
    }
  },
  methods: {
    handlePaymentChange(track) {
      track.trackPayment.freeDuration = 0
    },
    async fetchData() {
      this.loading = true
      try {
        const result = await trackService.queryTrackDetail(this.trackId)
        this.initTrack(result)
      } catch (error) {
        this.$alert(`查询异常！${error.message}`, '提示', { type: 'error', error })
      } finally {
        this.loading = false
      }
    },
    initTrack(track) {
      const trackObj = new Song({
        ...track,
        singerList: track.trackSingerList,
        payment: track.trackPayment || { /* paymentUpdate: 0 */ },
      })

      trackObj.album = track.album

      trackObj.lockCopyright = true
      trackObj.lockMusic = true
      trackObj.lockWord = true
      trackObj.lockBasic = true

      trackObj.mediaFinish = true
      trackObj.uploadPercent = 0
      trackObj.buTrace = track.buTrace || {}

      const trackCopyright = trackObj.trackCopyright
      trackObj.trackCopyright = this.toFrontend(trackCopyright)

      const wordList = []
      for (const tc of trackObj.trackWordCopyrightList) {
        wordList.push(this.toFrontend(tc))
      }
      trackObj.trackWordCopyrightList = wordList

      const musicList = []
      for (const tc of trackObj.trackMusicCopyrightList) {
        musicList.push(this.toFrontend(tc))
      }
      trackObj.trackMusicCopyrightList = musicList

      // 把已经处理过的数据保存一份, 最后对比时使用
      trackObj.oldLockObj = Object.freeze(cloneDeep({
        firstPublicTime: trackObj.firstPublicTime,
        trackPayment: trackObj.trackPayment,
        trackCopyright: trackObj.trackCopyright,
        trackWordCopyrightList: trackObj.trackWordCopyrightList,
        trackMusicCopyrightList: trackObj.trackMusicCopyrightList,
        // 核心基础信息
        trackName: trackObj.trackName,
        trackSingerList: trackObj.trackSingerList,
        transName: trackObj.transName,
        srcVersion: trackObj.srcVersion,
        trackExtend: {
          subTitle: trackObj.trackExtend.subTitle,
          wordAuthor: trackObj.trackExtend.wordAuthor,
          musicAuthor: trackObj.trackExtend.musicAuthor,
        },
        trackLyricistList: trackObj.trackLyricistList,
        trackComposerList: trackObj.trackComposerList,
        publicTime: trackObj.publicTime,
        media: trackObj.media,
        lyricData: trackObj.lyricData
      }))

      fileFieldConfigList.forEach(config => {
        if (!trackObj[config.key]) {
          trackObj[config.key] = []
        }
      })

      this.song = trackObj
      this.howlerData = { src: [trackObj?.media?.mediaUrl] }
    },

    handleShowFlow() {
      this.flowTrack = this.song
      this.showFlow = true
    },

    handleCloseFlow() {
      this.showFlow = false
      this.flowTrack = null
    },

    handleMediaProgress(event, file, fileList) {
      this.song.uploadPercent = event.percent
    },

    /**
     * @param {Object} file 和其他 file 对象属性一样,多了 md5 这个值
     */
    handleMediaBefore(file) {
      if (!validateFileExt(trackType, file.name)) {
        this.$message.warning('不支持的文件类型')
        return Promise.reject()
      }

      this.song.mediaFinish = false
    },

    handleMediaError(error, file, fileList) {
      this.$alert(`<${file.name}>上传失败，请稍后重试`, '提示', {
        error,
        type: 'error',
      })
      this.song.mediaFinish = true
    },

    handleMediaSuccess(resp, file, fileList) {
      const respData = resp.data
      this.song.mediaFinish = true // 表示音频上传完成, 无论有没严重错误
      this.song.hasError = isAudioCheckError(respData.file_info.audio_check)
      this.song.uploadPercent = 0
      this.song.media = {
        mediaUrl: respData.url,
        mediaPath: respData.file_id,
        using: 1,
        remarks: this.fileRemarks,
        fileType: file.name.slice(file.name.lastIndexOf('.') + 1),
        fileSize: file.size,
        fileName: file.name,
        md5: respData.md5,
        duration: respData.file_info.duration != null ? respData.file_info.duration : 0,
        checkQuality: respData.file_info.audio_check,
      }

      this.howlerData = { src: [respData.url] }
    },

    toFrontend(trackCopyright) {
      return copyrightToFE.call(this, trackCopyright)
    },

    checkSubGenre(val) {
      return this.song.genre.includes(Math.floor(val / 100))
    },

    getLyricData() {
      const ld = {}
      const lyricData = this.$refs.lyric.getLyricData()
      for (const lyricType of lyricList) {
        if (lyricData[lyricType.key].fileList[0]) {
          ld[lyricType.key] = lyricData[lyricType.key].fileList[0]
          ld[lyricType.key].fFrom = lyricData[lyricType.key].from
        } else {
          ld[lyricType.key] = null
        }
      }

      return ld
    },

    handleChangeGenre(val) {
      this.song.trackExtend.subGenre = this.song.trackExtend.subGenre.filter(item => {
        return this.checkSubGenre(item)
      })
    },

    handleDateChange(time, fieldName) {
      const timeObj = dayjs(time, 'YYYY-MM-DD HH:mm:ss')
      if (timeObj.isBefore(dayjs())) {
        this.$alert(`<b>${fieldName}</b>早于当前时间，请确定输入是否正确`, '提示', {
          type: 'warning',
          dangerouslyUseHTMLString: true
        })
      }
    },

    originCompanySearch(copyrightType) {
      return (queryString, cb) => {
        if (copyrightType === 'editCopyright') {
          this.editCopyright.originCpCompany.companyId = void (0)
        } else {
          this.song[copyrightType].originCpCompany.companyId = void (0)
        }
        const originCompanyList = this.originCompanyList
        const results = queryString ? originCompanyList.filter(this.createFilter(queryString)) : originCompanyList
        // 调用 callback 返回建议列表的数据
        cb(results)
      }
    },

    handleOriginCompanySelect(copyrightType, item) {
      if (copyrightType === 'editCopyright') {
        this.editCopyright.originCpCompany.companyName = item.companyName
        this.editCopyright.originCpCompany.companyId = item.companyId
      } else {
        this.song[copyrightType].originCpCompany.companyName = item.companyName
        this.song[copyrightType].originCpCompany.companyId = item.companyId
      }
    },
    createFilter(queryString) {
      return (company) => {
        return (company.companyName.toLowerCase().indexOf(queryString.toLowerCase()) === 0)
      }
    },

    /**
     * 申请修改歌曲特殊字段
     */
    handleApply() {
      const lockKey = `lock${this.activeTab.replace(/^[a-z]/, word => word.toUpperCase())}`
      if (this.song[lockKey] === false) {
        return this.$message({
          type: 'info',
          message: '已经解锁'
        })
      }
      this.$alert('指定(带锁图标)的字段修改将会生成字段修改申请单，需要审批通过才能生效，请谨慎操作！', '提示', {
        type: 'warning'
      }).then(_ => {
        this.song[lockKey] = false
      }).catch(_ => {
        //
      })
    },

    handleEditClose() {
      this.editCopyright = null
      this.dialogShow = false
    },

    handleAddWM(type) {
      this.dialogShow = true
      this.editType = type
      this.editCopyright = {
        tempId: Date.now(),
        ...cloneDeep(defaultWMCopyright)
      }
    },

    handleRemove(type, index) {
      this.song[type === 'word' ? 'trackWordCopyrightList' : 'trackMusicCopyrightList'].splice(index, 1)
    },

    handleEdit(type, row) {
      this.dialogShow = true
      this.editType = type
      if (!row.tempId) {
        row.tempId = Date.now()
      }
      this.editCopyright = {
        musicFileList: [],
        wordFileList: [],
        ...cloneDeep(row)
      }
    },

    handleSaveEdit() {
      this.$refs.editCopyright.validate().then(async valid => {
        const { editType, editCopyright } = this
        const copyrightList = this.song[editType === 'word' ? 'trackWordCopyrightList' : 'trackMusicCopyrightList']
        const existIndex = copyrightList.findIndex(item => item.tempId === editCopyright.tempId)

        if (existIndex > -1) {
          copyrightList.splice(existIndex, 1, editCopyright)
        } else {
          copyrightList.push(editCopyright)
        }
        this.editCopyright = null
        this.dialogShow = false
      }).catch(_ => {})
    },

    handleReset() {
      this.fetchData()
    },

    handleSave() {
      this.$refs.trackForms.validate(async(valid) => {
        if (!valid) return

        if (!this.song.mediaFinish) {
          return this.$alert('歌曲音频文件未上传完成, 请完成后提交', '提示')
        }
        if (this.song.hasError) {
          return this.$alert('歌曲音频文件存在严重问题, 请重新上传', '提示')
        }

        const trackObj = cloneDeep(this.song)
        trackObj.lyricData = this.getLyricData()

        // 乐器信息，如果没有填写值不要提交到后台
        const insData = this.$refs.inst.getData()
        const tempObj = {}
        insData
          .filter(item => item.value)
          .forEach(item => {
            tempObj[item.key] = item.value
          })
        trackObj.trackInstrument = tempObj

        // 歌曲使用了艺人的几个字段处理
        this._transformSinger(trackObj.trackSingerList)
        this._transformSinger(trackObj.trackComposerList)
        this._transformSinger(trackObj.trackLyricistList)
        this._transformSinger(trackObj.trackArrangerList)
        this._transformSinger(trackObj.trackProducerList)
        trackObj.srcGenre = ''
        trackObj.albumObj = undefined
        trackObj.genre = trackObj.genre.join(',')
        trackObj.albumId = trackObj.albumId ? trackObj.albumId.split(' -@-')[0] : 0
        trackObj.trackExtend.subGenre = trackObj.trackExtend.subGenre.join(',')
        trackObj.srcVersion = trackObj.srcVersion ?? ''

        const lockKeys = ['lockCopyright', 'lockMusic', 'lockWord', 'lockBasic']
        const changeLockField = lockKeys.some(key => trackObj[key] === false)
        if (trackObj.trackId && changeLockField) {
          // 解锁了, 检查是否有字段变化
          // 当前逻辑是哪怕字段修改申请 请求异常，还是可以修改专辑提交成功的
          try {
            await checkAndApplyModify(trackObj)
          } catch (error) {
            return
          }

          // 需要审核的字段，提交时需要以 旧的值 提交
          const newWordIdList = trackObj.trackWordCopyrightList.map(wc => wc.id)
          const newMusicIdList = trackObj.trackMusicCopyrightList.map(mc => mc.id)
          // 扩展字段只重置审核字段
          const trackExtend = Object.assign({}, trackObj.trackExtend, trackObj.oldLockObj.trackExtend)
          Object.assign(trackObj, trackObj.oldLockObj, { trackExtend })

          // 词曲版权, 判断是否有删除,有的话直接去掉
          trackObj.trackWordCopyrightList = trackObj.trackWordCopyrightList.filter(wc => newWordIdList.includes(wc.id))
          trackObj.trackMusicCopyrightList = trackObj.trackMusicCopyrightList.filter(mc => newMusicIdList.includes(mc.id))
        }
        trackObj.oldLockObj = undefined
        trackObj.trackLicenseList = []

        // 把专辑的版权公司赋予到歌曲的录音、词曲版权上
        // 转化版权字段格式
        trackObj.trackWordCopyrightList = trackObj.trackWordCopyrightList.map(right => {
          return cpToBackend(right)
        })
        trackObj.trackMusicCopyrightList = trackObj.trackMusicCopyrightList.map(right => {
          return cpToBackend(right)
        })

        trackObj.trackCopyrightCompetitor = trackObj.trackCopyrightCompetitor || {}
        if (!trackObj.trackCopyrightCompetitor.cpCompanyId) {
          trackObj.trackCopyrightCompetitor.cpCompanyId = trackObj.trackCopyright.cpCompany && trackObj.trackCopyright.cpCompany.companyId
        }

        cpToBackend(trackObj.trackCopyright)

        this.save(trackObj)
      })
    },

    async save(postData) {
      this.loading = true
      try {
        await trackService.saveTrack(postData)
        this.$autoCloseMsg(3000, {
          type: 'success',
          message: '您的修改已成功提交，如涉及敏感字段修改还需审核！3秒后转跳回列表页。'
        })

        setTimeout(_ => {
          this.$router.push({
            name: 'trackList',
          })
        }, 3000)
      } catch (error) {
        this.$alert(`提交异常（${error.message}）`, '提示', { type: 'error', error })
      } finally {
        this.loading = false
      }
    },
    /**
     * 排序, 统一字段
     * @param {Array} singerList - 歌曲艺人对象数组
     * @
     */
    _transformSinger(singerList) {
      for (let i = 0; i < singerList.length; i++) {
        const singer = singerList[i]
        singer.orderIndex = i
        singer.albumList = undefined
      }
    },
  }
}
</script>

<style lang="scss">
.song-item {
  position: relative;

  .el-form {
    position: relative;
  }

  .form-field {
    width: 400px;
  }

  .area-info {
    margin-left: 8px;

    &:hover {
      color: #5cb6ff;
      cursor:help;
    }
  }
  .sync-cp {
    position: absolute;
    right: 10px;
  }
  .apply-icon {
    position: absolute;
    top: 10px;
    right: 20px;
    cursor: pointer;
    color: red;
  }
}

</style>
